// eslint-disable-next-line import/no-extraneous-dependencies
import * as _ from 'lodash';
import moment from 'moment';
import { LEAGUES } from '../constants/leagues';

function compareNum(a, b) {
  return a - b;
}

function markLastInRound(matchesByRound) {
  const roundItemKeys = Object.keys(matchesByRound);

  roundItemKeys.forEach((roundItemKey) => {
    const roundItems = matchesByRound[roundItemKey];
    roundItems[roundItems.length - 1].lastInRound = true;
  });

  return matchesByRound;
}

export function getMatchesPrefixedByRound(matches) {
  let matchesByRound = _.groupBy(matches, 'round');
  matchesByRound = markLastInRound(matchesByRound);

  const rounds = Object.keys(matchesByRound).sort(compareNum);
  const result = rounds.reduce((acc, round) => {
    acc.push({
      type: 'round',
      id: `round-${round}`,
      isHeader: true,
      round,
    });
    return acc.concat(matchesByRound[round]);
  }, []);
  return result;
}

export function getMatchesPrefixedByWeek(matches) {
  const matchesWithWeek = matches.map((match) => {
    const week = moment(match.startDate).week();
    return {
      ...match,
      week,
    };
  });

  let matchesByWeek = _.groupBy(matchesWithWeek, 'week');
  matchesByWeek = markLastInRound(matchesByWeek);

  const weeks = Object.keys(matchesByWeek).sort(compareNum);
  const result = weeks.reduce((acc, week) => {
    acc.push({
      type: 'week',
      id: `week-${week}`,
      isHeader: true,
      week,
    });
    return acc.concat(matchesByWeek[week]);
  }, []);
  return result;
}

export function getMatchesPrefixedByKickOff(matches) {
  const matchesWithKickoff = matches.map((match) => {
    const kickoff = moment(match.startDate).format('HH:mm');
    return {
      ...match,
      kickoff,
    };
  });

  let matchesByKickoff = _.groupBy(matchesWithKickoff, 'kickoff');
  matchesByKickoff = markLastInRound(matchesByKickoff);

  const kickoffs = Object.keys(matchesByKickoff).sort(compareNum);
  const result = kickoffs.reduce((acc, kickoff) => {
    acc.push({
      type: 'kickoff',
      id: `kickoff-${kickoff}`,
      isHeader: true,
      kickoff,
    });
    return acc.concat(matchesByKickoff[kickoff]);
  }, []);
  return result;
}

export function getCurrentMatchesPrefixed(matches) {
  const matchesWithSortId = matches.map((match) => {
    const isToday = moment(match.startDate).isSame(moment(), 'day');
    const sortId = isToday
      ? moment(match.startDate).format('YYYY MM DD HH:mm')
      : moment(match.startDate).format('YYYYMMDD');
    return {
      ...match,
      sortId,
    };
  });

  let matchesBySortId = _.groupBy(matchesWithSortId, 'sortId');

  matchesBySortId = markLastInRound(matchesBySortId);

  const sortIds = Object.keys(matchesBySortId).sort((a, b) =>
    moment(a).diff(moment(b))
  );
  const result = sortIds.reduce((acc, sortId) => {
    acc.push({
      id: `sort-${sortId}`,
      isHeader: true,
      sortId,
    });
    return acc.concat(matchesBySortId[sortId]);
  }, []);

  return result;
}

export function orderMatchesByDateDiff(matches) {
  return matches
    .map((m) => {
      // If match is finished use finishDate otherwise use startDate
      const diff =
        matches.status === 'FINISHED'
          ? moment(m.finishedDate).diff(moment(), 'hours')
          : moment(m.startDate).diff(moment(), 'hours');

      /**
       * Ignore if date comparing is upcoming or past. Making all diff-values positive will do this.
       */
      const posDiff = Math.abs(diff);
      return { ...m, diff: posDiff };
    })
    .sort((m1, m2) => m1.diff - m2.diff);
}

export function orderMatchesByDate(matches) {
  return matches
    .map((m) => {
      // If match is finished use finishDate otherwise use startDate
      const diff =
        matches.status === 'FINISHED'
          ? moment(m.finishedDate).diff(moment(), 'hours')
          : moment(m.startDate).diff(moment(), 'hours');

      return { ...m, diff };
    })
    .sort((m1, m2) => m1.diff - m2.diff);
}

export function getNiceDayAndDateForMatchlist(date) {
  const day = moment(date).format('dddd').toUpperCase();
  const month = moment(date).format('MMMM').toUpperCase();
  const dateAsNumber = moment(date).format('D');

  return `${day} ${dateAsNumber} ${month}`;
}

export function getCurrentMatches(
  matches,
  upcomingMatchesLimit,
  finishedMatchesLimit
) {
  // Ignore limits per status for now to avoid complex logic and missing out showing matches.
  const limit = upcomingMatchesLimit + finishedMatchesLimit;

  const lastRoundMatches = matches.filter((match) => match.round === 30);

  if (lastRoundMatches.length <= limit) {
    const sameDayMatches = matches.filter((match) =>
      moment().isSame(match.startDate, 'day')
    );

    // If number of matches today is less than limit get additional matches with start/finish date closest.
    if (sameDayMatches.length < limit) {
      // Filter out upcoming or finished matches not same day.
      const notSameDayMatches = orderMatchesByDateDiff(
        matches.filter(
          (match) =>
            !moment().isSame(match.startDate, 'day') &&
            match.status !== 'INTERRUPTED'
        )
      );
      const additionalMatchesLimit = limit - sameDayMatches.length;
      const additionalMatches = notSameDayMatches.slice(
        0,
        additionalMatchesLimit
      );

      return orderMatchesByDate(sameDayMatches.concat(additionalMatches));
    }

    // Ignore limit for matches starting/finished same day.
    return sameDayMatches;
  }

  if (
    lastRoundMatches.filter((m) => m.status === 'FINISHED').length ===
    lastRoundMatches.length
  ) {
    const sliceOfLastRoundMatches = lastRoundMatches.slice(
      0,
      lastRoundMatches.length
    );

    return sliceOfLastRoundMatches;
  }

  return lastRoundMatches;
}

export function getArenaBg(homeTeamAbbrv) {
  const bgBasePath =
    'https://cl-match-card-bgs-sthlm.s3.eu-north-1.amazonaws.com';
  const arenabg = `${bgBasePath}/${homeTeamAbbrv}-WEB.jpg`;

  return arenabg;
}

let timeOffset = 0;

export function hasTimeOffset() {
  return timeOffset !== 0;
}

export function getCurrentMoment() {
  return moment().subtract(timeOffset);
}

export function getStartOfToday(currentMoment = getCurrentMoment()) {
  return moment({
    year: currentMoment.year(),
    month: currentMoment.month(),
    date: currentMoment.date(),
  });
}

export const getSplitDuration = (secondsLeftToStart, startDate) => {
  const days = startDate
    ? getStartOfToday(moment(startDate)).diff(getStartOfToday(), 'days')
    : 0;
  const duration = moment.duration(secondsLeftToStart, 'seconds');
  const hours = Math.floor(duration.asHours()) % 24;
  const minutes = Math.floor(duration.asMinutes()) % 60;
  const seconds = Math.floor(duration.asSeconds()) % 60;

  return {
    days: days > 0 ? days : null,
    hours: days > 0 || hours > 0 ? hours : null,
    minutes: days > 0 || hours > 0 || minutes > 0 ? minutes : null,
    seconds:
      days > 0 || hours > 0 || minutes > 0 || seconds >= 0 ? seconds : null,
  };
};

export const getArenaName = (match) => {
  const arenaName = _.get(match, 'arenaName', '');

  if (match?.configLeagueName === LEAGUES.FUTSAL) {
    return arenaName.toUpperCase().replace('IDROTTSOMRÅDE', '');
  }

  return arenaName;
};
